import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtModal from "../../components/NtModal";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import { useTheme } from "../../context/ThemeContext";
import { getAuthService } from "@ntconnect/framework";
import { showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";

/**
 * Lists every active device session for the signed-in user and lets them
 * sign individual ones out (or sign out everyone except this device in one
 * click). Backed by GET/POST/DELETE /auth/sessions on the BFF.
 */
function NtSettingsDevicesCard() {
  const { theme } = useTheme();
  const styles = makeStyles(theme);

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  // Per-row sign-out confirm + revoke-others confirm both reuse the same
  // confirm-alert modal pattern as the Sign-out row up the page.
  const revokeOneModalRef = React.useRef(null);
  const revokeOthersModalRef = React.useRef(null);
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, mine] = await Promise.all([
        getAuthService().businessListSessions(),
        getAuthService().businessCurrentDeviceId(),
      ]);
      Logger("Settings · listSessions", { count: list.length, mine });
      setSessions(Array.isArray(list) ? list : []);
      setCurrentDeviceId(mine || null);
    } catch (e) {
      Logger("Settings · listSessions failed", e);
      showToast("Couldn't load devices", e?.message || "Try again later.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmRevokeOne = (s) => {
    setPendingRevoke(s);
    revokeOneModalRef.current?.show();
  };

  const handleRevokeOne = async () => {
    const s = pendingRevoke;
    revokeOneModalRef.current?.dismiss();
    if (!s) return;
    setBusyId(s.deviceId);
    try {
      await getAuthService().businessRevokeSession(s.deviceId);
      showToast("Device signed out", `${s.deviceLabel || s.deviceId} can no longer access your account.`, "success");
      await load();
    } catch (e) {
      showToast("Couldn't sign out", e?.message || "Try again later.", "error");
    } finally {
      setBusyId(null);
      setPendingRevoke(null);
    }
  };

  const handleRevokeOthers = async () => {
    revokeOthersModalRef.current?.dismiss();
    if (!currentDeviceId) {
      showToast(
        "No device id yet",
        "Sign in again first so we can keep this device signed in.",
        "error"
      );
      return;
    }
    setRevokingOthers(true);
    try {
      const revoked = await getAuthService().businessRevokeOtherSessions(currentDeviceId);
      showToast(
        "Other devices signed out",
        revoked === 1 ? "1 device was signed out." : `${revoked} devices were signed out.`,
        "success"
      );
      await load();
    } catch (e) {
      showToast("Couldn't sign out others", e?.message || "Try again later.", "error");
    } finally {
      setRevokingOthers(false);
    }
  };

  const hasOthers = sessions.some((s) => s.deviceId !== currentDeviceId);

  return (
    <View style={styles.card}>
      <View style={styles.head}>
        <View style={{ flex: 1 }}>
          <NtText style={styles.headTitle}>Signed-in devices</NtText>
          <NtText style={styles.headSub}>
            Each row is a device that currently has a valid session. Sign one
            out and it'll be locked out on its next request.
          </NtText>
        </View>
        <NtTouchableEffect
          onPress={() => revokeOthersModalRef.current?.show()}
          style={[styles.signOutOthers, (!hasOthers || revokingOthers) && styles.signOutOthersDisabled]}
        >
          <NtMaterialIcon name="logout-variant" size={14} color={theme.brand2} />
          <NtText style={styles.signOutOthersText}>
            {revokingOthers ? "Signing out…" : "Sign out other devices"}
          </NtText>
        </NtTouchableEffect>
      </View>

      {!loading && sessions.length > 0 && !currentDeviceId && (
        <View style={styles.noticeBanner}>
          <NtMaterialIcon name="information-outline" size={14} color={theme.orange} />
          <NtText style={styles.noticeText}>
            We can't tell which row is this device — sign out and sign back in once
            to tag it. From then on this device will be marked clearly.
          </NtText>
        </View>
      )}

      <View style={styles.body}>
        {loading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={theme.brand2} />
            <NtText style={styles.loadingText}>Loading sessions…</NtText>
          </View>
        ) : sessions.length === 0 ? (
          <NtText style={styles.emptyText}>No active sessions.</NtText>
        ) : (
          (() => {
            // Sort the current device to the top so it's always the first row.
            // Falls through to original order when we can't identify it.
            const sorted = currentDeviceId
              ? [...sessions].sort((a, b) =>
                  a.deviceId === currentDeviceId ? -1 : b.deviceId === currentDeviceId ? 1 : 0,
                )
              : sessions;
            return sorted.map((s, i) => {
            const isMe = s.deviceId === currentDeviceId;
            const isBusy = busyId === s.deviceId;
            return (
              <View
                key={s.deviceId}
                style={[
                  styles.row,
                  i < sorted.length - 1 && styles.rowDivider,
                  isMe && styles.rowMe,
                ]}
              >
                <View style={styles.rowIcon}>
                  <NtMaterialIcon name={iconFor(s.deviceType)} size={18} color={theme.brand2} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <View style={styles.rowTitleLine}>
                    <NtText style={styles.rowTitle} numberOfLines={1}>
                      {s.deviceLabel || "Unknown device"}
                    </NtText>
                    {isMe && (
                      <View style={styles.mePill}>
                        <NtText style={styles.mePillText}>This device</NtText>
                      </View>
                    )}
                    {!s.deviceLabel && (
                      <View style={styles.legacyPill}>
                        <NtText style={styles.legacyPillText}>Legacy session</NtText>
                      </View>
                    )}
                  </View>
                  <NtText style={styles.rowMeta} numberOfLines={1}>
                    {[
                      s.deviceType || (s.deviceLabel ? null : "id " + shortId(s.deviceId)),
                      s.ipLastSeen,
                      formatRelative(s.lastUsedAt),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </NtText>
                </View>
                <NtTouchableEffect
                  onPress={() => confirmRevokeOne(s)}
                  style={[styles.rowAction, (isMe || isBusy) && styles.rowActionDisabled]}
                  // Disable revoking the current device — Sign-out up the
                  // page is the right tool for that.
                >
                  {isBusy ? (
                    <ActivityIndicator size="small" color={theme.red} />
                  ) : (
                    <NtText style={[styles.rowActionText, isMe && { color: theme.textMute }]}>
                      Sign out
                    </NtText>
                  )}
                </NtTouchableEffect>
              </View>
            );
            });
          })()
        )}
      </View>

      <NtModal ref={revokeOneModalRef}>
        <NtModalConfirmAlert
          title="Sign out this device?"
          message={
            pendingRevoke?.deviceLabel
              ? `${pendingRevoke.deviceLabel} will be signed out immediately.`
              : "This device will be signed out immediately."
          }
          onCancel={() => {
            revokeOneModalRef.current?.dismiss();
            setPendingRevoke(null);
          }}
          onAccept={handleRevokeOne}
        />
      </NtModal>

      <NtModal ref={revokeOthersModalRef}>
        <NtModalConfirmAlert
          title="Sign out other devices?"
          message="Every device except this one will be signed out. You'll stay signed in here."
          onCancel={() => revokeOthersModalRef.current?.dismiss()}
          onAccept={handleRevokeOthers}
        />
      </NtModal>
    </View>
  );
}

// Mapping from BFF deviceType strings to MaterialCommunityIcons names.
// `web` covers Safari / Chrome / etc.; everything else is best-effort.
function iconFor(deviceType) {
  switch ((deviceType || "").toLowerCase()) {
    case "ios":
    case "ipad":
      return "cellphone-iphone";
    case "android":
      return "cellphone-android";
    case "macos":
      return "laptop";
    case "windows":
      return "monitor";
    case "linux":
      return "linux";
    case "web":
    default:
      return "web";
  }
}

// First + last 4 chars of a UUID so the row still has something locatable.
function shortId(id) {
  if (!id) return "";
  if (id.length <= 12) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

// Render a friendly "x minutes ago" for last-used, falling back to the raw
// ISO string when the timestamp is malformed.
function formatRelative(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  return new Date(t).toLocaleDateString();
}

const makeStyles = (t) =>
  StyleSheet.create({
    card: {
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: 12,
      overflow: "hidden",
    },
    head: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12,
      paddingHorizontal: 14,
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: t.borderSoft,
    },
    headTitle: { color: t.text, fontSize: 13, fontWeight: "700" },
    headSub: { color: t.textDim, fontSize: 11, marginTop: 2, lineHeight: 16 },
    signOutOthers: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: t.pillBorder,
      backgroundColor: t.pillBg,
    },
    signOutOthersDisabled: { opacity: 0.4 },
    signOutOthersText: { color: t.brand2, fontSize: 11, fontWeight: "700" },

    body: { paddingHorizontal: 4, paddingVertical: 4 },
    loadingRow: { flexDirection: "row", alignItems: "center", gap: 10, padding: 14 },
    loadingText: { color: t.textDim, fontSize: 12 },
    emptyText: { color: t.textDim, fontSize: 12, padding: 14 },

    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingHorizontal: 10,
      paddingVertical: 12,
    },
    rowMe: {
      backgroundColor: t.pillBg,
      borderLeftWidth: 3,
      borderLeftColor: t.brand2,
      paddingLeft: 7,
    },
    rowDivider: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: t.borderSoft,
    },
    noticeBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 10,
      backgroundColor: "rgba(241,178,80,0.10)",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: t.borderSoft,
    },
    noticeText: { flex: 1, fontSize: 11.5, color: t.text, lineHeight: 16 },
    rowIcon: {
      width: 32,
      height: 32,
      borderRadius: 8,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
    },
    rowTitleLine: { flexDirection: "row", alignItems: "center", gap: 8 },
    rowTitle: { color: t.text, fontSize: 13, fontWeight: "600", flexShrink: 1 },
    mePill: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 6,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
    },
    mePillText: { color: t.brand2, fontSize: 9.5, fontWeight: "700", letterSpacing: 0.4 },
    legacyPill: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 6,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
    },
    legacyPillText: { color: t.textMute, fontSize: 9.5, fontWeight: "600", letterSpacing: 0.4 },
    rowMeta: { color: t.textDim, fontSize: 11, marginTop: 2 },
    rowAction: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 6,
    },
    rowActionDisabled: { opacity: 0.4 },
    rowActionText: { color: t.red, fontSize: 12, fontWeight: "600" },
  });

export default NtSettingsDevicesCard;
